import { queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import { api } from "./api";

/**
 * The session bootstrap.
 *
 * One query, read by the route guards and by the nav: who you are, whether
 * onboarding is done, and which verticals you coordinate. `null` means signed
 * out — that is an answer, not an error, so it resolves rather than throws and
 * the guard can redirect instead of showing an error boundary.
 */
async function fetchMe() {
  const { data, error } = await api.me.get();
  if (error) {
    if (error.status === 401) return null;
    throw new Error(`Could not load your session (HTTP ${error.status}).`);
  }
  return data;
}

export const meQueryOptions = queryOptions({
  queryKey: ["me"],
  queryFn: fetchMe,
  // Coordinator grants are re-resolved server-side on every request, so this
  // copy only drives navigation. Half a minute keeps the nav honest without
  // a request per click.
  staleTime: 30_000,
  retry: false,
});

export type Me = NonNullable<Awaited<ReturnType<typeof fetchMe>>>;
export type Profile = Me["profile"];

/**
 * The session, as components must read it.
 *
 * NOT `useRouteContext({ from: "/_auth" })`. The guard's return value is
 * computed once in `beforeLoad` and is frozen for the life of the match, so a
 * component reading it shows the profile as it was when the route loaded —
 * upload a resume and the page still says there isn't one. Reading the query
 * subscribes to the cache instead, so every mutation's `invalidateMe` reaches
 * the screen.
 *
 * The guard has already resolved this query and redirected when it was null,
 * so by the time anything under `_auth` renders, the cache is warm and
 * non-null. The throw is for the impossible case, not a branch anyone hits.
 */
export function useMe(): Me {
  const { data } = useQuery(meQueryOptions);
  if (!data) throw new Error("useMe() used outside the authenticated layout");
  return data;
}

/** Called after anything that can change the profile or the session. */
export function invalidateMe(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: ["me"] });
}
